import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  AiAgentKnowledgeBase,
  AiAgentKnowledgeBaseOrderByInput,
  AiAgentsNamespaceCreateKnowledgeBaseArgs,
  Mutation,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ResponsiveButton } from 'src/components/general/button/lgg-button';
import { TableLayoutPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import {
  getSortDirectionByOrder,
  SorterData,
  TableSortData,
} from 'src/components/general/table-helpers';
import { TableLayoutPageContainer } from 'src/components/layout/table-layout-page-container';
import { AiAgentsListLayout } from 'src/components/pages/ai-agents/components/ai-agents-list-layout';
import {
  AiAgentsListVariables,
  useAiAgentsListQueryParams,
} from 'src/components/pages/ai-agents/hooks/use-ai-agents-list-query-params';
import { KnowledgeBasesTable } from 'src/components/pages/ai-agents/knowledge-bases/components/knowledge-bases-table';
import { useDeleteKnowledgeBase } from 'src/components/pages/ai-agents/knowledge-bases/hooks/use-delete-knowledge-base';
import {
  CREATE_KNOWLEDGE_BASE_MUTATION,
  KNOWLEDGE_BASES_LIST_QUERY,
} from 'src/components/pages/ai-agents/knowledge-bases/queries';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const DEFAULT_SORT_DATA: TableSortData = { key: 'title', direction: 'ASC' };

const resolveOrderBy = (sortData: TableSortData): AiAgentKnowledgeBaseOrderByInput =>
  sortData.key === 'createdBy'
    ? { createdBy: { firstName: sortData.direction } }
    : { [sortData.key]: sortData.direction };

type KnowledgeBasesListPageProps = {
  sectionUrl: string;
};

export const KnowledgeBasesListPage = memo<KnowledgeBasesListPageProps>(
  ({ sectionUrl }) => {
    const { t } = useTranslation(['aiAgents']);
    const history = useHistory();
    const { id: companyId } = useCurrentInstitution();
    const { hasPermission } = useAuthorization();
    const handleGraphQLError = useHandleGraphQLError();
    const openDelete = useDeleteKnowledgeBase({ refetchList: true });
    const { handleNextPage, handlePreviousPage, handleSortChange, sortData, variables } =
      useAiAgentsListQueryParams({ defaultSortData: DEFAULT_SORT_DATA, resolveOrderBy });

    const { data, loading } = useQuery<
      Pick<Query, 'aiAgents'>,
      AiAgentsListVariables<AiAgentKnowledgeBaseOrderByInput>
    >(KNOWLEDGE_BASES_LIST_QUERY, { variables, onError: handleGraphQLError });

    const [createKnowledgeBase, { loading: creating }] = useMutation<
      Pick<Mutation, 'aiAgents'>,
      AiAgentsNamespaceCreateKnowledgeBaseArgs
    >(CREATE_KNOWLEDGE_BASE_MUTATION, {
      onError: handleGraphQLError,
      onCompleted: ({ aiAgents }) =>
        history.push(`${sectionUrl}/knowledge-bases/${aiAgents.createKnowledgeBase.id}`),
    });

    if (!data || loading) {
      return (
        <TableLayoutPageContainer>
          <TableLayoutPlaceholder />
        </TableLayoutPageContainer>
      );
    }

    const { nodes, totalCount, pageInfo } = data.aiAgents.listKnowledgeBases;

    return (
      <AiAgentsListLayout
        testId="knowledge-bases-list-page"
        totalCount={totalCount}
        entityName={t('aiAgents:pages.knowledgeBases.count', { count: totalCount })}
        pageInfo={pageInfo}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        actions={
          hasPermission('ai:company.ai_agent_knowledge_base:create') ? (
            <ResponsiveButton
              data-lgg-id="knowledge-bases-list-page-add-button"
              variant="cta"
              size="regular"
              icon="add"
              disabled={creating}
              onClick={() =>
                createKnowledgeBase({
                  variables: {
                    input: {
                      companyId,
                      title: t('aiAgents:pages.knowledgeBases.untitled'),
                    },
                  },
                })
              }
            >
              {t('aiAgents:pages.knowledgeBases.addButton')}
            </ResponsiveButton>
          ) : undefined
        }
      >
        <KnowledgeBasesTable
          data={nodes}
          sortData={sortData}
          sectionUrl={sectionUrl}
          onDelete={openDelete}
          onChange={(pagination, filters, sorter) => {
            const { columnKey, order } = sorter as SorterData<AiAgentKnowledgeBase>;

            handleSortChange({
              key: columnKey,
              direction: getSortDirectionByOrder(order),
            });
          }}
        />
      </AiAgentsListLayout>
    );
  },
);
